import React, { useState, useRef, useEffect } from "react";
import "./YearSelect.css";

export interface YearSelectProps {
  value: number;
  onChange: (year: number) => void;
  options: number[];
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
}

const YearSelect: React.FC<YearSelectProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  id = "year-select",
  "aria-label": ariaLabel = "Selecionar ano",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const sortedYears = [...options].sort((a, b) => b - a);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        setIsOpen((prev) => !prev);
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) setIsOpen(true);
        else {
          const idx = sortedYears.indexOf(value);
          if (idx < sortedYears.length - 1) onChange(sortedYears[idx + 1]);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) setIsOpen(true);
        else {
          const idx = sortedYears.indexOf(value);
          if (idx > 0) onChange(sortedYears[idx - 1]);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`year-select ${isOpen ? "year-select--open" : ""} ${disabled ? "year-select--disabled" : ""}`}
      id={id}
    >
      <button
        type="button"
        className="year-select__trigger"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span id={`${id}-label`} className="year-select__value">
          {value}
        </span>
        <span className="year-select__chevron" aria-hidden>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <ul
          className="year-select__dropdown"
          role="listbox"
          aria-label={ariaLabel}
          aria-activedescendant={`${id}-option-${value}`}
          tabIndex={0}
        >
          {sortedYears.map((year) => (
            <li
              key={year}
              id={`${id}-option-${year}`}
              role="option"
              aria-selected={year === value}
              className={`year-select__option ${year === value ? "year-select__option--selected" : ""}`}
              onClick={() => {
                onChange(year);
                setIsOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(year);
                  setIsOpen(false);
                }
              }}
            >
              {year}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default YearSelect;
